import React, { useEffect, useState } from 'react';
import { FaClock, FaMapMarkerAlt, FaSearch, FaTruck } from 'react-icons/fa';
import { SHIPPING_ZONES } from '../data/shipping';
import { getShippingSchedule } from '../utils/shippingSchedule';
import styles from './styles/ShippingPage.module.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface AddressSuggestion {
  placeId: string;
  description: string;
  mainText?: string;
  secondaryText?: string;
}

interface ZoneResult {
  shippingZoneId: string;
  mapZoneName: string;
  formattedAddress: string;
}

const ShippingPage: React.FC = () => {
  const [address, setAddress] = useState('');
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [addressFocused, setAddressFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [result, setResult] = useState<ZoneResult | null>(null);

  useEffect(() => {
    const query = address.trim();
    if (!addressFocused || query.length < 3) {
      setSuggestions([]);
      return;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const response = await fetch(
          `${API_URL}/shipping-address/suggestions?q=${encodeURIComponent(query)}`,
          { signal: controller.signal }
        );
        const data = await response.json();
        setSuggestions(response.ok && Array.isArray(data) ? data : []);
      } catch {
        if (!controller.signal.aborted) setSuggestions([]);
      }
    }, 300);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [address, addressFocused]);

  const detectZone = async (selectedAddress = address) => {
    const query = selectedAddress.trim();
    if (query.length < 3) {
      setMessage('Ingresá una calle y su altura para consultar.');
      setResult(null);
      return;
    }

    setLoading(true);
    setMessage('Buscando tu dirección...');
    setResult(null);
    setSuggestions([]);

    try {
      const response = await fetch(`${API_URL}/shipping-zone/detect?address=${encodeURIComponent(query)}`);
      const data = await response.json();
      if (response.ok && data?.detected && data?.shippingZoneId) {
        setResult({
          shippingZoneId: data.shippingZoneId,
          mapZoneName: data.mapZoneName || 'Zona de envío detectada',
          formattedAddress: data.formattedAddress || query
        });
        setMessage('');
      } else {
        setMessage(data?.error || 'No pudimos determinar la zona para esa dirección.');
      }
    } catch {
      setMessage('No pudimos consultar el nomenclador municipal. Intentá nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const selectSuggestion = (suggestion: AddressSuggestion) => {
    setAddress(suggestion.description);
    setAddressFocused(false);
    setSuggestions([]);
    void detectZone(suggestion.description);
  };

  const shippingZone = result
    ? SHIPPING_ZONES.find(zone => zone.id === result.shippingZoneId)
    : undefined;
  const schedule = result ? getShippingSchedule(result.mapZoneName, result.shippingZoneId) : [];

  return (
    <main className={styles['shipping-page']}>
      <section className={styles['shipping-panel']}>
        <div className={styles['shipping-heading']}>
          <span className={styles['heading-icon']}><FaTruck /></span>
          <p>Envíos a domicilio</p>
          <h1>Consultá tu zona y horario de entrega</h1>
          <span>Ingresá tu calle y altura. Te indicaremos dónde estás y cuándo podemos enviarte tu pedido.</span>
        </div>

        <form
          className={styles['address-form']}
          onSubmit={(event) => {
            event.preventDefault();
            void detectZone();
          }}
        >
          <label htmlFor="shipping-address">Dirección de entrega</label>
          <div className={styles['address-combobox']}>
            <div className={styles['search-row']}>
              <span className={styles['input-icon']}><FaMapMarkerAlt /></span>
              <input
                id="shipping-address"
                type="text"
                value={address}
                onChange={(event) => {
                  setAddress(event.target.value);
                  setResult(null);
                  setMessage('');
                }}
                onFocus={() => setAddressFocused(true)}
                onBlur={() => window.setTimeout(() => setAddressFocused(false), 180)}
                placeholder="Ej.: Mendoza 1950, Rosario"
                autoComplete="off"
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={suggestions.length > 0}
                aria-controls="shipping-page-address-suggestions"
              />
              <button type="submit" disabled={loading}>
                <FaSearch /> {loading ? 'Buscando...' : 'Consultar'}
              </button>
            </div>

            {addressFocused && suggestions.length > 0 && (
              <ul id="shipping-page-address-suggestions" className={styles['address-suggestions']} role="listbox">
                {suggestions.map(suggestion => (
                  <li key={suggestion.placeId}>
                    <button type="button" onMouseDown={() => selectSuggestion(suggestion)}>
                      <strong>{suggestion.mainText || suggestion.description}</strong>
                      {suggestion.secondaryText && <span>{suggestion.secondaryText}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <small>Elegí una sugerencia del nomenclador municipal para obtener el resultado más preciso.</small>
        </form>

        {message && <div className={styles['lookup-message']} role="status">{message}</div>}

        {result && (
          <section className={styles['result-card']} aria-live="polite">
            <div className={styles['zone-summary']}>
              <span className={styles['result-icon']}><FaMapMarkerAlt /></span>
              <div>
                <small>Tu zona de envío</small>
                <h2>{result.mapZoneName}</h2>
                <p>{result.formattedAddress}</p>
              </div>
            </div>

            <div className={styles['schedule-grid']}>
              {schedule.map(range => (
                <article key={range.label}>
                  <FaClock />
                  <div>
                    <small>{range.label}</small>
                    <strong>{range.time}</strong>
                    <span>{range.days}</span>
                  </div>
                </article>
              ))}
            </div>

            {shippingZone && (
              <div className={styles['shipping-cost']}>
                <span>En esta zona el envío cuesta <strong>${shippingZone.cost.toLocaleString('es-AR')}</strong>.</span>
                <span>Envío gratis a partir de <strong>${shippingZone.minPurchaseForFree.toLocaleString('es-AR')}</strong>.</span>
              </div>
            )}
            <p className={styles['sunday-note']}>No realizamos envíos los domingos.</p>
          </section>
        )}
      </section>
    </main>
  );
};

export default ShippingPage;
